/**
 * Environment Configuration
 * All environment-specific values centralized here
 * Load from .env files - NO hardcoded values in code
 */

export const ENV = {
  // Browser Branding (from legacy .precision.* env)
  BROWSER_CLIENT_NAME:
    process.env.REACT_APP_CLIENT_FOR_BROWSER ||
    process.env.CLIENT_FOR_BROWSER ||
    process.env.REACT_APP_NAME ||
    'MDM',
  BROWSER_LOGO_FILE:
    process.env.REACT_APP_LOGO_FOR_BROWSER ||
    process.env.LOGO_FOR_BROWSER ||
    '',

  // API Configuration
  API_BASE_URL: (() => {
    const raw = process.env.REACT_APP_API_BASE_URL;
    if (raw && raw.trim()) {
      return raw.trim();
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'http://localhost:3000';
  })(),
  // API_TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
  
  // Authentication
  AUTH_COOKIE_NAME: process.env.REACT_APP_AUTH_COOKIE_NAME || 'jwt',
  AUTH_TOKEN_KEY: process.env.REACT_APP_AUTH_TOKEN_KEY || 'authToken',
  AUTH_USER_KEY: process.env.REACT_APP_AUTH_USER_KEY || 'user',
  TOKEN_REFRESH_INTERVAL: parseInt(process.env.REACT_APP_TOKEN_REFRESH_INTERVAL || '1800000', 10), // 30 minutes
  AUTO_LOGIN_USERNAME: process.env.REACT_APP_AUTO_LOGIN_USERNAME || '',
  AUTO_LOGIN_PASSWORD: process.env.REACT_APP_AUTO_LOGIN_PASSWORD || '',
  SESSION_DURATION_MS: 5 * 60 * 60 * 1000, // 5 hours — must match backend JWT expiresIn
  
  // Application
  APP_NAME: process.env.REACT_APP_NAME || 'MDM UI',
  APP_VERSION: process.env.REACT_APP_VERSION || '1.0.0',
  APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
  
  // Feature Flags
  FEATURE_FLAGS: {
    newMatching: process.env.REACT_APP_FEATURE_NEW_MATCHING === 'true',
    advancedSearch: process.env.REACT_APP_FEATURE_ADVANCED_SEARCH === 'true',
    exportEnabled: process.env.REACT_APP_FEATURE_EXPORT !== 'false', // Default true
    analyticsEnabled: process.env.REACT_APP_ANALYTICS === 'true',
    debugMode: process.env.REACT_APP_DEBUG === 'true'
  },
  
  // Logging
  LOG_LEVEL: process.env.REACT_APP_LOG_LEVEL || 'error', // debug, info, warn, error
  ENABLE_CONSOLE_LOGS: process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true',
  
  // Pagination
  DEFAULT_PAGE_SIZE: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE || '10', 10),
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100, 200],
  
  // UI Configuration
  DEBOUNCE_DELAY: parseInt(process.env.REACT_APP_DEBOUNCE_DELAY || '300', 10),
  TOAST_AUTO_CLOSE: parseInt(process.env.REACT_APP_TOAST_AUTO_CLOSE || '5000', 10),
  
  // Development
  IS_DEVELOPMENT: process.env.NODE_ENV === 'development',
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  IS_TEST: process.env.NODE_ENV === 'test',
  ENABLE_OFFLINE_LOGIN: process.env.REACT_APP_ENABLE_OFFLINE_LOGIN
    ? process.env.REACT_APP_ENABLE_OFFLINE_LOGIN === 'true'
    : process.env.NODE_ENV === 'development'
};

/**
 * Resolve browser logo path from env value.
 * Supports:
 * - full URLs (http/https)
 * - absolute paths (/img/logo.png)
 * - relative paths (img/logo.png)
 * - legacy filenames from mdm_ui (AceMDM-logo.png -> /img/AceMDM-logo.png)
 *
 * @param {string} logoFile
 * @returns {string}
 */
export const resolveBrowserLogoPath = (logoFile = ENV.BROWSER_LOGO_FILE) => {
  if (!logoFile) return '';

  if (logoFile.startsWith('http://') || logoFile.startsWith('https://')) {
    return logoFile;
  }

  if (logoFile.startsWith('/')) {
    return logoFile;
  }

  if (logoFile.includes('/')) {
    return `/${logoFile}`;
  }

  return `/img/${logoFile}`;
};

/**
 * Validate required environment variables
 * Call this on app initialization to catch config errors early
 */
export const validateEnvironment = () => {
  const required = [];
  
  const missing = required.filter(key => !process.env[key]);
  
  if (missing.length > 0) {
    console.error('Missing required environment variables:', missing);
    if (ENV.IS_PRODUCTION) {
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }
  
  return missing.length === 0;
};

/**
 * Get feature flag status
 * 
 * @param {string} featureName - Name of the feature flag
 * @returns {boolean} - Feature enabled status
 */
export const isFeatureEnabled = (featureName) => {
  return ENV.FEATURE_FLAGS[featureName] || false;
};

/**
 * Log configuration info (development only)
 */
export const logEnvironmentInfo = () => {
  if (!ENV.IS_DEVELOPMENT) return;

  if (ENV.ENABLE_CONSOLE_LOGS) {
    const envInfo = {
      environment: ENV.APP_ENVIRONMENT,
      apiBaseUrl: ENV.API_BASE_URL,
      appVersion: ENV.APP_VERSION,
      featureFlags: ENV.FEATURE_FLAGS,
    };
    window.__MDM_ENV_INFO__ = envInfo;
  }
};
